import { notify } from "@general-backoffice/core";
import type { Charge, StrapiId, User } from "@investhinks/api-client";
import moment from "moment";
import React, { type CSSProperties, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button, Input, Modal, ModalBody, Spinner } from "reactstrap";

import { chargesEndpoints } from "../../api";
import getErrorMessage from "../../utils/getErrorMessage";
import currencySymbol from "../utils/currency";

const DEFAULT_REFUND_REASON = "";

// Local types for payment details
interface Payment {
  id?: number;
  pm_status?: string;
  pm_currency?: string;
  pm_amount?: number;
  pm_failureReason?: string;
  pm_risk_level?: string;
  createdAt?: string;
}

interface Refund {
  id?: number;
  pm_refundReason?: string;
  createdAt?: string;
}

// Define ChargeWithPayments without extending Charge to avoid type conflicts
// The Charge type from api-client may have stricter field definitions
interface ChargeWithPayments {
  id: StrapiId;
  pm_status?: string;
  pm_currency?: string;
  pm_amount?: number;
  pm_periodStart?: string;
  pm_periodEnd?: string;
  pm_failureReason?: string;
  pm_bank_message?: string;
  pm_refunded?: boolean;
  pm_payments?: Payment[];
  pm_refunds?: Refund[];
  periodProfit?: number;
  [key: string]: unknown;
}

interface PaymentDetailsModalProps {
  isOpen: boolean;
  toggle: () => void;
  charge: ChargeWithPayments | Charge | null;
  user: User | null;
  onRefundSuccess?: () => void;
}

interface StatusConfig {
  color: string;
  bg: string;
  label: string;
}

const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({ isOpen, toggle, charge, user, onRefundSuccess }) => {
  const { t } = useTranslation("charges", { keyPrefix: "paymentDetails" });
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState(DEFAULT_REFUND_REASON);
  const [isRefunding, setIsRefunding] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  if (!charge) return null;

  const payments = charge.pm_payments || [];
  const refund = charge.pm_refunds?.[0];
  const isAdmin = user?.role?.name === "Admin";
  const isZeroAmount = !charge.pm_amount || charge.pm_amount <= 0;
  const canRefund = isAdmin && charge.pm_status === "completed" && !charge.pm_refunded && !isZeroAmount;
  const showZeroAmountNotice = isAdmin && charge.pm_status === "completed" && !charge.pm_refunded && isZeroAmount;

  const handleRefund = async () => {
    if (!password) {
      setRefundError(t("refund.passwordRequired"));
      return;
    }
    setIsRefunding(true);
    setRefundError(null);
    try {
      await chargesEndpoints.adminRefund({ chargeId: charge.id, password, reason: reason.trim() });
      notify.success(t("refund.success"));
      setShowRefundConfirm(false);
      setPassword("");
      setReason(DEFAULT_REFUND_REASON);
      if (onRefundSuccess) onRefundSuccess();
      toggle();
    } catch (error: unknown) {
      setRefundError(getErrorMessage(error, t("refund.error")));
    } finally {
      setIsRefunding(false);
    }
  };

  const handleCloseRefund = () => {
    setShowRefundConfirm(false);
    setPassword("");
    setReason(DEFAULT_REFUND_REASON);
    setRefundError(null);
  };

  const getDeclineReasonMessage = (declineReason: string | undefined | null): string => {
    if (!declineReason) return "";
    const translationKey = isAdmin ? `declineReasons.admin.${declineReason}` : `declineReasons.user.${declineReason}`;
    const translated = t(translationKey, { defaultValue: "" });
    return String(translated) || (isAdmin ? declineReason : t("declineReasons.user._fallback"));
  };

  const statusConfig: Record<string, StatusConfig> = {
    pending: { color: "#ffffff", bg: "#6b7280", label: t("statuses.pending") },
    processing: { color: "#ffffff", bg: "#3b82f6", label: t("statuses.processing") },
    completed: { color: "#ffffff", bg: "#10b981", label: t("statuses.completed") },
    failed: { color: "#ffffff", bg: "#ef4444", label: t("statuses.failed") },
    declined: { color: "#ffffff", bg: "#ef4444", label: t("statuses.declined") },
    cancelled: { color: "#ffffff", bg: "#f59e0b", label: t("statuses.cancelled") },
  };

  const getStatus = (status: string | null | undefined): StatusConfig =>
    statusConfig[status || "pending"] || statusConfig.pending;
  const chargeStatus = getStatus(charge.pm_status);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalBody className="p-0">
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.invoiceLabel}>{t("title", { invoiceNumber: "" }).replace(" - Invoice #", "")}</div>
            <div style={styles.invoiceNumber}>#{String(charge.pm_invoiceNumber ?? "")}</div>
          </div>
          <button type="button" onClick={toggle} style={styles.closeButton} aria-label={t("close")} title={t("close")}>
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        {/* Status Banner */}
        {charge.pm_refunded ? (
          <div style={styles.refundedBanner}>
            <div style={styles.refundedIcon}>
              <i className="fas fa-undo" />
            </div>
            <div style={styles.refundedContent}>
              <div style={styles.refundedHeader}>
                <div style={styles.refundedTitle}>{t("refund.alreadyRefunded")}</div>
                <div style={styles.refundedDate}>
                  {refund?.createdAt ? moment(refund.createdAt).format("DD MMM YYYY, HH:mm") : ""}
                </div>
              </div>
              {refund?.pm_refundReason && (
                <div style={styles.refundedReason}>
                  <span style={styles.reasonLabel}>{t("refund.reason")}:</span> {refund.pm_refundReason}
                </div>
              )}
              {!refund?.pm_refundReason && <div style={styles.refundedReasonEmpty}>{t("refund.noReason")}</div>}
            </div>
          </div>
        ) : (
          <div style={{ ...styles.statusBanner, backgroundColor: chargeStatus.bg }}>
            <div style={{ ...styles.statusDot, backgroundColor: chargeStatus.color }} />
            <span style={{ color: chargeStatus.color, fontWeight: 600 }}>{chargeStatus.label}</span>
          </div>
        )}

        {/* Main Content */}
        <div style={styles.content}>
          {/* Amount Card */}
          <div style={styles.amountCard}>
            <div style={styles.amountLabel}>{t("amount")}</div>
            <div style={styles.amountValue}>
              {currencySymbol[charge.pm_currency || ""] || charge.pm_currency || ""}
              {charge.pm_amount?.toFixed(2)}
            </div>
            <div style={styles.periodText}>
              {moment(charge.pm_periodStart).format("DD MMM")} — {moment(charge.pm_periodEnd).format("DD MMM YYYY")}
            </div>
          </div>

          {/* Details Grid */}
          <div style={styles.detailsGrid}>
            {charge.periodProfit !== undefined && (
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>{t("profits")}</div>
                <div style={styles.detailValue}>
                  {currencySymbol[charge.pm_currency || ""] || charge.pm_currency || ""}
                  {charge.periodProfit?.toFixed(2)}
                </div>
              </div>
            )}
            <div style={styles.detailItem}>
              <div style={styles.detailLabel}>{t("paymentAttempts")}</div>
              <div style={styles.detailValue}>{payments.length}</div>
            </div>
          </div>

          {/* Failure Alert */}
          {charge.pm_failureReason && (
            <div style={styles.failureAlert}>
              <i className="fas fa-exclamation-circle" style={styles.failureIcon} />
              <div>
                <div style={styles.failureTitle}>{t("chargeFailureReason")}</div>
                <div style={styles.failureMessage}>{getDeclineReasonMessage(charge.pm_failureReason)}</div>
                {isAdmin && charge.pm_bank_message && <div style={styles.bankMessage}>{charge.pm_bank_message}</div>}
              </div>
            </div>
          )}

          {/* Payment Attempts */}
          {payments.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>{t("paymentAttempts")}</div>
              <div style={styles.paymentsList}>
                {payments.map((payment, index) => {
                  const paymentStatus = getStatus(payment.pm_status);
                  const hasFailed = payment.pm_status === "failed" || payment.pm_status === "declined";

                  return (
                    <div key={payment.id || index} style={styles.paymentItem}>
                      <div style={styles.paymentMain}>
                        <div style={styles.paymentLeft}>
                          <div style={{ ...styles.paymentStatusDot, backgroundColor: paymentStatus.bg }} />
                          <div>
                            <div style={styles.paymentAmount}>
                              {currencySymbol[payment.pm_currency || ""] || payment.pm_currency || ""}
                              {payment.pm_amount?.toFixed(2)}
                            </div>
                            <div style={styles.paymentDate}>
                              {payment.createdAt ? moment(payment.createdAt).format("DD MMM YYYY, HH:mm") : "-"}
                            </div>
                          </div>
                        </div>
                        <Badge
                          style={{
                            ...styles.paymentBadge,
                            backgroundColor: paymentStatus.bg,
                            color: paymentStatus.color,
                          }}
                        >
                          {paymentStatus.label}
                        </Badge>
                      </div>

                      {hasFailed && payment.pm_failureReason && (
                        <div style={styles.paymentFailure}>
                          <div style={styles.paymentFailureReason}>
                            {getDeclineReasonMessage(payment.pm_failureReason)}
                          </div>
                          {isAdmin && (
                            <div style={styles.paymentTechnical}>
                              <span style={styles.technicalCode}>{payment.pm_failureReason}</span>
                              {payment.pm_risk_level && (
                                <span
                                  style={{
                                    ...styles.riskBadge,
                                    backgroundColor: payment.pm_risk_level === "high" ? "#ef4444" : "#10b981",
                                    color: "#ffffff",
                                  }}
                                >
                                  {payment.pm_risk_level}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Admin Refund - Zero Amount Notice */}
          {showZeroAmountNotice && (
            <div style={styles.refundSection}>
              <div style={styles.adminPanel}>
                <div style={styles.adminPanelHeader}>
                  <div style={styles.adminIcon}>
                    <i className="fas fa-user-shield" />
                  </div>
                  <div>
                    <div style={styles.adminTitle}>{t("refund.adminActions")}</div>
                    <div style={styles.adminSubtitle}>{t("refund.zeroAmountNotice")}</div>
                  </div>
                </div>
                <Button color="link" disabled style={{ ...styles.refundButton, opacity: 0.5, cursor: "not-allowed" }}>
                  <i className="fas fa-undo" style={{ marginRight: 8 }} />
                  {t("refund.button")}
                </Button>
              </div>
            </div>
          )}

          {/* Admin Refund Section */}
          {canRefund && (
            <div style={styles.refundSection}>
              {!showRefundConfirm ? (
                <div style={styles.adminPanel}>
                  <div style={styles.adminPanelHeader}>
                    <div style={styles.adminIcon}>
                      <i className="fas fa-user-shield" />
                    </div>
                    <div>
                      <div style={styles.adminTitle}>{t("refund.adminActions")}</div>
                      <div style={styles.adminSubtitle}>{t("refund.subtitle")}</div>
                    </div>
                  </div>
                  <Button color="link" onClick={() => setShowRefundConfirm(true)} style={styles.refundButton}>
                    <i className="fas fa-undo" style={{ marginRight: 8 }} />
                    {t("refund.button")}
                  </Button>
                </div>
              ) : (
                <div style={styles.refundConfirm}>
                  <div style={styles.refundConfirmHeader}>
                    <i className="fas fa-exclamation-triangle" style={styles.refundWarningIcon} />
                    <div>
                      <div style={styles.refundConfirmTitle}>{t("refund.confirmTitle")}</div>
                      <div style={styles.refundConfirmSubtitle}>{t("refund.confirmMessage")}</div>
                    </div>
                  </div>

                  <div style={styles.refundAmountBox}>
                    <span style={styles.refundAmountLabel}>{t("refund.refundAmount")}</span>
                    <span style={styles.refundAmountValue}>
                      {currencySymbol[charge.pm_currency || ""] || charge.pm_currency || ""}
                      {charge.pm_amount?.toFixed(2)}
                    </span>
                  </div>

                  {/* Reason Input */}
                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>
                      <i className="fas fa-comment-alt" style={{ marginRight: 6 }} />
                      {t("refund.reasonLabel")}
                      <span style={styles.optionalBadge}>{t("refund.optional")}</span>
                    </label>
                    <Input
                      type="textarea"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={t("refund.reasonPlaceholder")}
                      disabled={isRefunding}
                      style={styles.reasonInput}
                      rows={2}
                    />
                    <div style={styles.inputHint}>{t("refund.reasonHint")}</div>
                  </div>

                  {refundError && (
                    <Alert color="danger" style={styles.refundError}>
                      {refundError}
                    </Alert>
                  )}

                  {/* Password Input */}
                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>
                      <i className="fas fa-lock" style={{ marginRight: 6 }} />
                      {t("refund.passwordLabel")}
                    </label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("refund.passwordPlaceholder")}
                      disabled={isRefunding}
                      onKeyPress={(e) => e.key === "Enter" && handleRefund()}
                      style={styles.passwordInput}
                    />
                  </div>

                  <div style={styles.refundActions}>
                    <Button
                      color="secondary"
                      outline
                      onClick={handleCloseRefund}
                      disabled={isRefunding}
                      style={styles.cancelButton}
                    >
                      {t("refund.cancel")}
                    </Button>
                    <Button
                      color="danger"
                      onClick={handleRefund}
                      disabled={isRefunding || !password}
                      style={styles.confirmButton}
                    >
                      {isRefunding ? <Spinner size="sm" /> : t("refund.confirm")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

const styles: Record<string, CSSProperties> = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    borderBottom: "1px solid #e5e7eb",
  },
  headerContent: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
  },
  invoiceLabel: {
    fontSize: 14,
    color: "#6b7280",
    fontWeight: 500,
  },
  invoiceNumber: {
    fontSize: 20,
    fontWeight: 700,
    color: "#111827",
    letterSpacing: "-0.02em",
  },
  closeButton: {
    background: "none",
    border: "none",
    padding: 8,
    cursor: "pointer",
    color: "#9ca3af",
    fontSize: 16,
    borderRadius: 6,
    transition: "all 0.15s",
  },
  statusBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 24px",
    fontSize: 14,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
  },
  refundedBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "16px 24px",
    backgroundColor: "#fef3c7",
    borderBottom: "1px solid #fcd34d",
  },
  refundedIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: "#f59e0b",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    flexShrink: 0,
  },
  refundedContent: {
    flex: 1,
    minWidth: 0,
  },
  refundedHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  refundedTitle: {
    fontWeight: 600,
    color: "#92400e",
    fontSize: 14,
  },
  refundedDate: {
    fontSize: 13,
    color: "#a16207",
  },
  refundedReason: {
    marginTop: 8,
    fontSize: 13,
    color: "#78350f",
    backgroundColor: "#fef9c3",
    padding: "8px 12px",
    borderRadius: 6,
    border: "1px solid #fde047",
  },
  reasonLabel: {
    fontWeight: 600,
  },
  refundedReasonEmpty: {
    marginTop: 8,
    fontSize: 13,
    color: "#a16207",
    fontStyle: "italic",
  },
  content: {
    padding: 24,
  },
  amountCard: {
    textAlign: "center",
    padding: "24px 0",
    marginBottom: 20,
  },
  amountLabel: {
    fontSize: 13,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 4,
  },
  amountValue: {
    fontSize: 36,
    fontWeight: 700,
    color: "#111827",
    letterSpacing: "-0.02em",
    lineHeight: 1.2,
  },
  periodText: {
    fontSize: 14,
    color: "#9ca3af",
    marginTop: 8,
  },
  detailsGrid: {
    display: "flex",
    gap: 16,
    marginBottom: 20,
  },
  detailItem: {
    flex: 1,
    padding: 16,
    backgroundColor: "#f9fafb",
    borderRadius: 8,
  },
  detailLabel: {
    fontSize: 12,
    color: "#6b7280",
    marginBottom: 4,
  },
  detailValue: {
    fontSize: 18,
    fontWeight: 600,
    color: "#111827",
  },
  failureAlert: {
    display: "flex",
    gap: 12,
    padding: 16,
    backgroundColor: "#fef2f2",
    borderRadius: 8,
    marginBottom: 20,
    border: "1px solid #fecaca",
  },
  failureIcon: {
    color: "#ef4444",
    fontSize: 18,
    flexShrink: 0,
    marginTop: 2,
  },
  failureTitle: {
    fontWeight: 600,
    color: "#991b1b",
    fontSize: 14,
    marginBottom: 4,
  },
  failureMessage: {
    color: "#b91c1c",
    fontSize: 14,
  },
  bankMessage: {
    color: "#7f1d1d",
    fontSize: 12,
    marginTop: 8,
    fontFamily: "monospace",
    backgroundColor: "#fee2e2",
    padding: "4px 8px",
    borderRadius: 4,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 12,
  },
  paymentsList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  paymentItem: {
    padding: 16,
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
  },
  paymentMain: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  paymentLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  paymentStatusDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
  },
  paymentAmount: {
    fontWeight: 600,
    color: "#111827",
    fontSize: 15,
  },
  paymentDate: {
    fontSize: 13,
    color: "#6b7280",
  },
  paymentBadge: {
    fontSize: 12,
    fontWeight: 500,
    padding: "4px 10px",
    border: "none",
  },
  paymentFailure: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: "1px solid #e5e7eb",
  },
  paymentFailureReason: {
    fontSize: 13,
    color: "#ef4444",
    marginBottom: 8,
  },
  paymentTechnical: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },
  technicalCode: {
    fontSize: 11,
    fontFamily: "monospace",
    backgroundColor: "#e5e7eb",
    padding: "2px 6px",
    borderRadius: 4,
    color: "#4b5563",
  },
  riskBadge: {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 4,
    fontWeight: 500,
  },
  refundSection: {
    marginTop: 24,
    paddingTop: 24,
    borderTop: "1px solid #e5e7eb",
  },
  adminPanel: {
    backgroundColor: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: 8,
    padding: 16,
  },
  adminPanelHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  adminIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: "#0ea5e9",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
  },
  adminTitle: {
    fontWeight: 600,
    color: "#0c4a6e",
    fontSize: 14,
  },
  adminSubtitle: {
    fontSize: 12,
    color: "#0369a1",
  },
  refundButton: {
    color: "#ef4444",
    padding: 0,
    fontSize: 14,
    fontWeight: 500,
  },
  refundConfirm: {
    padding: 20,
    backgroundColor: "#fef2f2",
    borderRadius: 8,
    border: "1px solid #fecaca",
  },
  refundConfirmHeader: {
    display: "flex",
    gap: 12,
    marginBottom: 16,
  },
  refundWarningIcon: {
    color: "#ef4444",
    fontSize: 20,
    flexShrink: 0,
  },
  refundConfirmTitle: {
    fontWeight: 600,
    color: "#991b1b",
    fontSize: 15,
  },
  refundConfirmSubtitle: {
    fontSize: 13,
    color: "#b91c1c",
  },
  refundAmountBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "white",
    borderRadius: 6,
    marginBottom: 16,
    border: "1px solid #fecaca",
  },
  refundAmountLabel: {
    fontSize: 13,
    color: "#6b7280",
  },
  refundAmountValue: {
    fontSize: 18,
    fontWeight: 700,
    color: "#ef4444",
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 6,
  },
  optionalBadge: {
    marginLeft: 8,
    fontSize: 11,
    fontWeight: 500,
    color: "#6b7280",
    backgroundColor: "#e5e7eb",
    padding: "2px 6px",
    borderRadius: 4,
  },
  reasonInput: {
    fontSize: 14,
    resize: "none",
  },
  inputHint: {
    fontSize: 12,
    color: "#9ca3af",
    marginTop: 4,
  },
  refundError: {
    marginBottom: 16,
    fontSize: 13,
  },
  passwordInput: {
    fontSize: 14,
  },
  refundActions: {
    display: "flex",
    gap: 12,
  },
  cancelButton: {
    flex: 1,
  },
  confirmButton: {
    flex: 1,
  },
};

export default PaymentDetailsModal;
